import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import i18n from '../i18n';
import { ChatSocketContext } from './ChatSocketContext';
import { AuthContext } from '../Header/auth - context';

// Deo S — poziv (audio/video) preko WebRTC-a. Signalizacija ide preko ISTOG socket-a
// koji chat već koristi (ChatSocketContext) — nema druge konekcije. Server u
// TMS_Backend/socket/chatSocket.js je čist relay (offer/answer/ICE), mediji idu
// peer-to-peer (ili preko TURN-a) direktno između dva browsera.
//
// Ograničeno na 1:1 pozive (isti obrazac kao DM u chatu) — poziva se SAMO iz otvorenog
// razgovora (idGrupe mora postojati, server proverava da je pozivalac član te grupe).
//
// Redosled signalizacije (pogledati chatSocket.js za server-side pandan):
//   pozivalac:  getUserMedia -> emit call-invite (BEZ SDP-a još)
//   primalac:   prikaže dolazni poziv; na Accept -> getUserMedia + emit call-accept
//   pozivalac:  na call-accepted -> napravi RTCPeerConnection + offer -> emit webrtc-offer
//   primalac:   na webrtc-offer -> setRemoteDescription + answer -> emit webrtc-answer
//   pozivalac:  na webrtc-answer -> setRemoteDescription
//   oba: onicecandidate -> emit webrtc-ice-candidate (i buffer dok pc ne postoji)

export const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

const genCallId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const CallProvider = ({ children }) => {
    const { socketRef, socket } = useContext(ChatSocketContext) || {};
    const auth = useContext(AuthContext);

    // 'idle' | 'outgoing' | 'incoming' | 'active'
    const [callState, setCallState] = useState('idle');
    const [callInfo, setCallInfo] = useState(null); // { callId, idGrupe, callType, peerId, peerTip, peerName }
    const [localStream, setLocalStream] = useState(null);
    const [remoteStream, setRemoteStream] = useState(null);
    const [muted, setMuted] = useState(false);
    const [cameraOff, setCameraOff] = useState(false);
    const [connecting, setConnecting] = useState(false);
    // Ključ i18n poruke zašto veza nije uspela; null dok je sve u redu.
    const [greskaVeze, setGreskaVeze] = useState(null);

    /* ── Snimanje razgovora (21.10.2026) ──────────────────────────────────
       Snima ovaj računar — OBA glasa (mikrofon + sagovornik, spojeni u
       AudioContext-u). Sagovornik odmah dobija 'call-recording' i vidi
       „Razgovor se snima". Snimanje može da zatraži i telefon sagovornika
       (svi osim vozača) — tada snimamo mi, a odluku „sačuvaj / odbaci" donosi
       on na telefonu (snimak se šalje odmah po završetku, on ga može obrisati).
       Kad snimanje pokrene ovaj korisnik, posle poziva ga pita da li čuva. */
    const [snimam, setSnimam] = useState(false);
    const [snimaSagovornik, setSnimaSagovornik] = useState(false);
    const [snimakZaCuvanje, setSnimakZaCuvanje] = useState(null);   // { blob, trajanjeSek, callId, idGrupe, peerName }
    const snimanjeRef = useRef(null);   // { recorder, ctx, delovi, pocetak, pokrenuo, callId, idGrupe, peerName }

    const pcRef = useRef(null);
    const localStreamRef = useRef(null);
    const callInfoRef = useRef(null);
    const pendingCandidatesRef = useRef([]);
    const iceServersRef = useRef([{ urls: 'stun:stun.l.google.com:19302' }]);

    useEffect(() => { callInfoRef.current = callInfo; }, [callInfo]);
    const callStateRef = useRef('idle');
    useEffect(() => { callStateRef.current = callState; }, [callState]);
    useEffect(() => { localStreamRef.current = localStream; }, [localStream]);

    /* ICE server lista (STUN + TURN). TURN kredencijal ISTIČE (backend ga izdaje
       na 24 h), pa se lista osvežava i na početku svakog poziva — ne samo pri
       prijavi. Inače bi tab otvoren od juče nudio istekao kredencijal, TURN bi ga
       odbio, i poziv preko mobilnog interneta ne bi prošao bez ikakvog razloga
       vidljivog korisniku. */
    const osveziIceServere = useCallback(() => {
        if (!auth.token) return Promise.resolve();
        return fetch(process.env.REACT_APP_BACKEND_URL + '/chat/iceConfig', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
        })
            .then(r => (r.ok ? r.json() : null))
            .then(d => { if (d?.iceServers) iceServersRef.current = d.iceServers; })
            .catch(() => {});
    }, [auth.token]);

    useEffect(() => { osveziIceServere(); }, [osveziIceServere]);

    /* Ima li u listi ijedan TURN (relay) server. Bez njega poziv može da prođe
       samo ako su oba sagovornika na mreži koja dozvoljava direktnu vezu — na
       mobilnom internetu (CGNAT) to po pravilu nije slučaj. Razlika je bitna za
       poruku koju korisnik dobije: „pokušajte ponovo" nasuprot „sistem nema
       relay server, javite administratoru". */
    const jeRelayDostupan = useCallback(() => (iceServersRef.current || []).some((s) => {
        const u = s?.urls;
        const lista = Array.isArray(u) ? u : [u];
        return lista.some((x) => typeof x === 'string' && x.toLowerCase().startsWith('turn'));
    }), []);

    const stopLocalStream = () => {
        localStreamRef.current?.getTracks().forEach(t => t.stop());
        localStreamRef.current = null;
        setLocalStream(null);
    };

    const resetCall = useCallback(() => {
        /* Snimak se zatvara PRE gašenja tokova — ono što je snimljeno ostaje. */
        zaustaviSnimanjeRef.current?.();
        setSnimaSagovornik(false);
        if (pcRef.current) {
            pcRef.current.onicecandidate = null;
            pcRef.current.ontrack = null;
            pcRef.current.close();
            pcRef.current = null;
        }
        stopLocalStream();
        pendingCandidatesRef.current = [];
        setRemoteStream(null);
        setCallState('idle');
        setCallInfo(null);
        setMuted(false);
        setCameraOff(false);
        setConnecting(false);
        setGreskaVeze(null);
    }, []);

    const ensurePeerConnection = useCallback((callId) => {
        const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
        pc.onicecandidate = (e) => {
            if (e.candidate) socketRef?.current?.emit('webrtc-ice-candidate', { callId, candidate: e.candidate });
        };
        pc.ontrack = (e) => setRemoteStream(e.streams[0]);
        pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'connected') { setConnecting(false); setGreskaVeze(null); return; }

            /* Ranije se obrađivalo SAMO 'connected'. Kad ICE ne uspe — a bez TURN
               relaya to je pravilo čim su sagovornici na različitim mrežama —
               prozor poziva je ostajao zauvek na „Povezivanje…", bez ijedne poruke.
               Korisnik to vidi kao „poziv ne radi", a zapravo je signalizacija
               prošla i tek medija veza nije mogla da se uspostavi. */
            if (pc.connectionState === 'failed') {
                setConnecting(false);
                setGreskaVeze(jeRelayDostupan()
                    ? 'vezaNijeUspela'
                    : 'vezaNijeUspelaBezRelaya');
                /* Poruka ostaje par sekundi da se pročita, pa se poziv ZAVRŠI. Ranije
                   je samo stajala poruka, a poziv i mikrofon ostajali otvoreni zauvek. */
                setTimeout(() => {
                    if (callInfoRef.current?.callId !== callId || pcRef.current !== pc) return;
                    socketRef?.current?.emit('call-end', { callId });
                    resetCall();
                }, 6000);
            }
        };
        pcRef.current = pc;
        /* Kandidati koji stignu pre nego što pc ima opis druge strane se NE dodaju
           ovde — addIceCandidate bi ih odbio (i to tiho). Drže se u baferu i puštaju
           tek posle setRemoteDescription (vidi pustiBaferovane). */
        return pc;
    }, [socketRef, resetCall, jeRelayDostupan]);

    const pustiBaferovane = (pc) => {
        const lista = pendingCandidatesRef.current;
        pendingCandidatesRef.current = [];
        lista.forEach(c => pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {}));
    };

    /* Signalizacija koja pukne usred uspostavljanja (offer/answer) — poziv se
       završi uz poruku, umesto da zauvek stoji na „Povezivanje…". */
    const prekiniZbogGreske = useCallback((callId) => {
        if (callInfoRef.current?.callId !== callId) return;
        socketRef?.current?.emit('call-end', { callId });
        resetCall();
        window.alert(i18n.t('chat.callGreskaVeze'));
    }, [socketRef, resetCall]);

    // ── Pozivalac: pokreni poziv iz otvorenog razgovora ──
    const startCall = useCallback(async ({ id, tip, name }, idGrupe, callType) => {
        if (!socketRef?.current) return;
        // Svež TURN kredencijal pre nego što veza nastane (pri javljanju druge strane).
        osveziIceServere();
        const constraints = callType === 'video' ? { audio: true, video: true } : { audio: true, video: false };
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (e) {
            window.alert(i18n.t('chat.callNemaMikrofona'));
            return;
        }
        const callId = genCallId();
        setLocalStream(stream);
        setCallInfo({ callId, idGrupe, callType, peerId: id, peerTip: tip, peerName: name });
        setCallState('outgoing');
        socketRef.current.emit('call-invite', { callId, idGrupe, targetId: id, targetTip: tip, callType });
    }, [socketRef, osveziIceServere]);

    const cancelCall = useCallback(() => {
        const info = callInfoRef.current;
        if (info) socketRef?.current?.emit('call-cancel', { callId: info.callId });
        resetCall();
    }, [socketRef, resetCall]);

    // ── Primalac: prihvati dolazni poziv ──
    const acceptCall = useCallback(async () => {
        const info = callInfoRef.current;
        if (!info || !socketRef?.current) return;
        const osvezavanje = osveziIceServere();
        const constraints = info.callType === 'video' ? { audio: true, video: true } : { audio: true, video: false };
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (e) {
            window.alert(i18n.t('chat.callNemaMikrofona'));
            socketRef.current.emit('call-reject', { callId: info.callId });
            resetCall();
            return;
        }
        /* Dok se čekala dozvola za mikrofon, poziv je mogao biti otkazan, preuzet
           na drugom uređaju ili istekao. Tada je resetCall već prošao — nastavak bi
           ostavio „aktivan" prozor bez sagovornika i zauzet mikrofon. */
        const josVazi = () => callInfoRef.current?.callId === info.callId;
        if (!josVazi()) { stream.getTracks().forEach(t => t.stop()); return; }
        setLocalStream(stream);
        setConnecting(true);
        setCallState('active');
        await osvezavanje;
        if (!josVazi()) { stream.getTracks().forEach(t => t.stop()); return; }
        const pc = ensurePeerConnection(info.callId);
        stream.getTracks().forEach(t => pc.addTrack(t, stream));
        socketRef.current.emit('call-accept', { callId: info.callId });
    }, [socketRef, ensurePeerConnection, resetCall, osveziIceServere]);

    const rejectCall = useCallback(() => {
        const info = callInfoRef.current;
        if (info) socketRef?.current?.emit('call-reject', { callId: info.callId });
        resetCall();
    }, [socketRef, resetCall]);

    const endCall = useCallback(() => {
        const info = callInfoRef.current;
        if (info) socketRef?.current?.emit('call-end', { callId: info.callId });
        resetCall();
    }, [socketRef, resetCall]);

    const toggleMute = useCallback(() => {
        const stream = localStreamRef.current;
        if (!stream) return;
        const next = !muted;
        stream.getAudioTracks().forEach(t => { t.enabled = !next; });
        setMuted(next);
    }, [muted]);

    const toggleCamera = useCallback(() => {
        const stream = localStreamRef.current;
        if (!stream) return;
        const next = !cameraOff;
        stream.getVideoTracks().forEach(t => { t.enabled = !next; });
        setCameraOff(next);
    }, [cameraOff]);

    // ── Socket listeneri za signalizaciju ──
    /* Zavisi od `socket` (stanje), NE od socketRef. Ref ima isti identitet zauvek,
       pa se efekat izvršavao samo jednom, pri montiranju — dok socket još nije
       postojao — i listeneri se nikad nisu zakačili. Pozivi su zato stizali u
       pregledač, ali nijedan nije zazvonio. */
    useEffect(() => {
        if (!socket) return;

        const onIncoming = ({ callId, idGrupe, callType, fromId, fromTip, fromName }) => {
            /* Već sam u pozivu na OVOM uređaju — novi se samo ne prikazuje. Ranije se
               slao call-reject, a on obara poziv za SVE uređaje: zauzeta web kartica
               bi odbila poziv koji bi telefon (slobodan) primio. Ako se niko ne javi,
               server ga ugasi posle 45 s kao „nema odgovora". */
            if (callInfoRef.current) return;
            setCallInfo({ callId, idGrupe, callType, peerId: fromId, peerTip: fromTip, peerName: fromName });
            setCallState('incoming');
        };

        const onAccepted = async ({ callId }) => {
            const info = callInfoRef.current;
            if (!info || info.callId !== callId) return;
            setConnecting(true);
            setCallState('active');
            const pc = ensurePeerConnection(callId);
            localStreamRef.current?.getTracks().forEach(t => pc.addTrack(t, localStreamRef.current));
            try {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                socket.emit('webrtc-offer', { callId, sdp: offer });
            } catch (e) {
                prekiniZbogGreske(callId);   // ranije prazan catch: večno „Povezivanje…"
            }
        };

        const onOffer = async ({ callId, sdp }) => {
            const pc = pcRef.current;
            if (!pc || callInfoRef.current?.callId !== callId) return;
            try {
                await pc.setRemoteDescription(new RTCSessionDescription(sdp));
                pustiBaferovane(pc);
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                socket.emit('webrtc-answer', { callId, sdp: answer });
            } catch (e) {
                prekiniZbogGreske(callId);
            }
        };

        const onAnswer = async ({ callId, sdp }) => {
            const pc = pcRef.current;
            if (!pc || callInfoRef.current?.callId !== callId) return;
            try {
                await pc.setRemoteDescription(new RTCSessionDescription(sdp));
                pustiBaferovane(pc);
            } catch (e) {
                prekiniZbogGreske(callId);
            }
        };

        const onIceCandidate = ({ callId, candidate }) => {
            if (callInfoRef.current?.callId !== callId || !candidate) return;
            /* Kandidat koji stigne pre opisa druge strane bi bio odbijen — čuva se
               do setRemoteDescription (puštaju ga onOffer/onAnswer). */
            if (pcRef.current?.remoteDescription) {
                pcRef.current.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
            } else {
                pendingCandidatesRef.current.push(candidate);
            }
        };

        const onFailed = ({ callId }) => {
            if (callInfoRef.current?.callId !== callId) return;
            window.alert(i18n.t('chat.callNedostupan'));
            resetCall();
        };
        const onCancelled = ({ callId }) => { if (callInfoRef.current?.callId === callId) resetCall(); };
        const onRejected  = ({ callId }) => { if (callInfoRef.current?.callId === callId) resetCall(); };
        const onEnded     = ({ callId }) => { if (callInfoRef.current?.callId === callId) resetCall(); };
        /* Javljeno sa drugog uređaja (npr. telefona). Dok samo zvoni — tiho ugasi
           zvono. Ako sam i ja upravo pritisnuo „Javi se" (drugi je bio brži), kaži
           zašto prozor nestaje, da ne izgleda kao da je poziv pukao. */
        const onAnsweredElsewhere = ({ callId }) => {
            const info = callInfoRef.current;
            if (!info || info.callId !== callId) return;
            const biloAktivno = callStateRef.current === 'active';
            resetCall();
            if (biloAktivno) window.alert(i18n.t('chat.callJavljenoDrugde'));
        };

        socket.on('call-incoming', onIncoming);
        socket.on('call-accepted', onAccepted);
        socket.on('webrtc-offer', onOffer);
        socket.on('webrtc-answer', onAnswer);
        socket.on('webrtc-ice-candidate', onIceCandidate);
        socket.on('call-failed', onFailed);
        socket.on('call-cancelled', onCancelled);
        socket.on('call-rejected', onRejected);
        socket.on('call-ended', onEnded);
        socket.on('call-answered-elsewhere', onAnsweredElsewhere);

        /* Socket se ponovo povezao posle prekida (WiFi↔LTE) — sa NOVIM id-jem. Server
           drži poziv 20 s (grace); ovim mu se javlja da je razgovor sada na ovom
           socketu. Bez toga bi svaki treptaj mreže prekinuo razgovor. */
        const onReconnect = () => {
            const info = callInfoRef.current;
            if (info && (callStateRef.current === 'active' || callStateRef.current === 'outgoing')) {
                socket.emit('call-rejoin', { callId: info.callId });
            }
        };
        socket.on('connect', onReconnect);

        return () => {
            socket.off('connect', onReconnect);
            socket.off('call-answered-elsewhere', onAnsweredElsewhere);
            socket.off('call-incoming', onIncoming);
            socket.off('call-accepted', onAccepted);
            socket.off('webrtc-offer', onOffer);
            socket.off('webrtc-answer', onAnswer);
            socket.off('webrtc-ice-candidate', onIceCandidate);
            socket.off('call-failed', onFailed);
            socket.off('call-cancelled', onCancelled);
            socket.off('call-rejected', onRejected);
            socket.off('call-ended', onEnded);
        };
    }, [socket, ensurePeerConnection, resetCall, prekiniZbogGreske]);

    /* Rokovi na klijentu — server ima svoje, ali ako signalizacija negde zastane
       (server restartovan, poruka izgubljena), prozor ne sme da stoji zauvek:
         zvoni / poziva  > 50 s  → kraj
         povezivanje     > 30 s  bez 'connected' → poruka i kraj */
    useEffect(() => {
        const info = callInfoRef.current;
        if (!info) return undefined;
        if (callState === 'incoming' || callState === 'outgoing') {
            const t = setTimeout(() => {
                if (callInfoRef.current?.callId !== info.callId) return;
                if (callStateRef.current === 'outgoing') socketRef?.current?.emit('call-cancel', { callId: info.callId });
                resetCall();
            }, 50000);
            return () => clearTimeout(t);
        }
        if (callState === 'active' && connecting) {
            const t = setTimeout(() => prekiniZbogGreske(info.callId), 30000);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [callState, connecting, resetCall, prekiniZbogGreske, socketRef]);

    /* ── Snimanje: početak, kraj, čuvanje ── */
    const zapocniSnimanje = useCallback((pokrenuo = null) => {
        const info = callInfoRef.current;
        const lokalni = localStreamRef.current;
        if (!info || !lokalni || !remoteStream || snimanjeRef.current || typeof MediaRecorder === 'undefined') return false;
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            const ctx = new Ctx();
            const izlaz = ctx.createMediaStreamDestination();
            [lokalni, remoteStream].forEach((tok) => {
                if (tok.getAudioTracks().length) ctx.createMediaStreamSource(new MediaStream(tok.getAudioTracks())).connect(izlaz);
            });
            const tip = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
                .find((m) => MediaRecorder.isTypeSupported?.(m)) || '';
            const recorder = new MediaRecorder(izlaz.stream, tip ? { mimeType: tip } : undefined);
            const delovi = [];
            recorder.ondataavailable = (e) => { if (e.data && e.data.size) delovi.push(e.data); };
            snimanjeRef.current = {
                recorder, ctx, delovi, pocetak: Date.now(), pokrenuo,
                callId: info.callId, idGrupe: info.idGrupe, peerName: info.peerName,
            };
            recorder.onstop = () => {
                const sn = snimanjeRef.current;
                snimanjeRef.current = null;
                setSnimam(false);
                try { sn?.ctx?.close(); } catch (e) { /* već zatvoren */ }
                if (!sn || !sn.delovi.length) return;
                const blob = new Blob(sn.delovi, { type: sn.recorder.mimeType || 'audio/webm' });
                const trajanjeSek = Math.round((Date.now() - sn.pocetak) / 1000);
                const zapis = { blob, trajanjeSek, callId: sn.callId, idGrupe: sn.idGrupe, peerName: sn.peerName, pokrenuo: sn.pokrenuo };
                /* Zatražio ga je sagovornik sa telefona → šalje se odmah; on odlučuje. */
                if (sn.pokrenuo) posaljiSnimakRef.current?.(zapis);
                else setSnimakZaCuvanje(zapis);
            };
            recorder.start(1000);
            setSnimam(true);
            socketRef?.current?.emit('call-recording', { callId: info.callId, snima: true });
            return true;
        } catch (e) {
            snimanjeRef.current = null;
            return false;
        }
    }, [remoteStream, socketRef]);

    const zaustaviSnimanje = useCallback(() => {
        const sn = snimanjeRef.current;
        if (!sn) return;
        socketRef?.current?.emit('call-recording', { callId: sn.callId, snima: false });
        try { if (sn.recorder.state !== 'inactive') sn.recorder.stop(); else sn.recorder.onstop?.(); } catch (e) { /* ugašen */ }
    }, [socketRef]);
    const zaustaviSnimanjeRef = useRef(null);
    zaustaviSnimanjeRef.current = zaustaviSnimanje;

    const posaljiSnimak = useCallback(async (zapis) => {
        const forma = new FormData();
        const ext = /ogg/.test(zapis.blob.type) ? 'ogg' : 'webm';
        forma.append('callId', zapis.callId);
        forma.append('id_grupe', String(zapis.idGrupe));
        forma.append('trajanjeSek', String(zapis.trajanjeSek));
        if (zapis.pokrenuo) {
            forma.append('pokrenuo_tip', zapis.pokrenuo.tip);
            forma.append('pokrenuo_id', String(zapis.pokrenuo.id));
        }
        forma.append('audio', zapis.blob, `poziv.${ext}`);
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/chat/snimakPoziva', {
            method: 'POST', headers: { Authorization: 'Bearer ' + auth?.token }, body: forma,
        });
        if (!r.ok) throw r;
        return r.json();
    }, [auth?.token]);
    const posaljiSnimakRef = useRef(null);
    posaljiSnimakRef.current = (zapis) => posaljiSnimak(zapis).catch(() => {});

    const toggleSnimanje = useCallback(() => {
        if (snimanjeRef.current) zaustaviSnimanje(); else zapocniSnimanje(null);
    }, [zapocniSnimanje, zaustaviSnimanje]);

    /* Dijalog posle poziva: sačuvaj / odbaci. */
    const sacuvajSnimak = useCallback(async () => {
        const z = snimakZaCuvanje;
        if (!z) return { ok: false };
        try { await posaljiSnimak(z); setSnimakZaCuvanje(null); return { ok: true }; }
        catch (e) { return { ok: false }; }
    }, [snimakZaCuvanje, posaljiSnimak]);
    const odbaciSnimak = useCallback(() => setSnimakZaCuvanje(null), []);

    useEffect(() => {
        if (!socket) return undefined;
        const naSnimanje = ({ callId, snima }) => {
            if (callInfoRef.current?.callId === callId) setSnimaSagovornik(!!snima);
        };
        const naZahtev = ({ callId, pokrenuo }) => {
            if (callInfoRef.current?.callId === callId) zapocniSnimanje(pokrenuo || null);
        };
        const naZaustavi = ({ callId }) => {
            if (snimanjeRef.current?.callId === callId) zaustaviSnimanje();
        };
        socket.on('call-recording', naSnimanje);
        socket.on('call-recording-start', naZahtev);
        socket.on('call-recording-stop', naZaustavi);
        return () => {
            socket.off('call-recording', naSnimanje);
            socket.off('call-recording-start', naZahtev);
            socket.off('call-recording-stop', naZaustavi);
        };
    }, [socket, zapocniSnimanje, zaustaviSnimanje]);

    return (
        <CallContext.Provider value={{
            callState, callInfo, localStream, remoteStream, muted, cameraOff, connecting, greskaVeze,
            startCall, cancelCall, acceptCall, rejectCall, endCall, toggleMute, toggleCamera,
            snimam, snimaSagovornik, snimakZaCuvanje, toggleSnimanje, sacuvajSnimak, odbaciSnimak,
        }}>
            {children}
        </CallContext.Provider>
    );
};
